/**
 * Login Page Component
 * Azure AD-authenticated login page with modern full-page design
 * Uses popup flow for reliable authentication
 */

import React, { useState, useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { loginRequest } from "../authConfig";
import { UserService } from "../services/UserService";
import logo from '../P2S_Legence_Logo_White.png';
import '../styles/Login.css';

const Login = ({ onLogin }) => {
  const { instance, inProgress } = useMsal();
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // On mount, clear any stale cached MSAL accounts so the user always
  // sees the "Sign in" button and picks the correct account via popup.
  useEffect(() => {
    const accounts = instance.getAllAccounts();
    accounts.forEach((acct) => {
      instance.setActiveAccount(null);
    });
  }, [instance]);

  const handleAzureLogin = async () => {
    try {
      setError("");
      setIsLoading(true);

      // loginPopup returns the auth result directly — no redirect needed
      const result = await instance.loginPopup(loginRequest);

      // Set the chosen account as active for subsequent token calls
      instance.setActiveAccount(result.account);
      const email = result.account.username;

      // Call the backend login endpoint (sends email for DB lookup, token for auth)
      const userData = await UserService.login(email);

      // Call the parent component's onLogin function
      onLogin(userData.name, email, userData.scheduled_hours || 40);
    } catch (err) {
      console.error("Azure AD login failed:", err);
      if (err.errorCode === "user_cancelled") {
        setError("Sign-in was cancelled. Please try again.");
      } else {
        setError(err.message || "Sign-in failed. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const isProcessing = isLoading || inProgress !== InteractionStatus.None;

  return (
    <div className="login-page">
      {/* Header Section */}
      <header className="login-header">
        <img 
          src={logo} 
          alt="P2S Logo" 
          className="login-header-logo"
        />
      </header>

      {/* Main Content Section */}
      <main className="login-main">
        <div className="login-container">
          <img 
            src={logo} 
            alt="P2S Logo" 
            className="login-logo"
          />
          
          {error && <div className="login-error">{error}</div>}
          
          <button 
            onClick={handleAzureLogin} 
            className="login-button"
            type="button"
            disabled={isProcessing}
          >
            {/* Microsoft Logo SVG */}
            <svg 
              className="microsoft-icon" 
              viewBox="0 0 23 23" 
              xmlns="http://www.w3.org/2000/svg"
              width="21"
              height="21"
            >
              <path fill="#f35325" d="M0 0h11v11H0z"/>
              <path fill="#81bc06" d="M12 0h11v11H12z"/>
              <path fill="#05a6f0" d="M0 12h11v11H0z"/>
              <path fill="#ffba08" d="M12 12h11v11H12z"/>
            </svg>
            <span>{isProcessing ? "Signing in..." : "Sign In with Microsoft"}</span>
          </button>
        </div>
      </main>

      {/* Footer Section */}
      <footer className="login-footer">
        <div className="login-footer-left">
          <div className="tooltip-container">
            <span className="footer-text">About</span>
            <div className="tooltip">
              Our Resource Allocation tool was developed by the P2S AI Team.
            </div>
          </div>
        </div>

        <div className="login-footer-right">
          <a 
            href="https://www.p2sinc.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="login-footer-link"
          >
            www.p2sinc.com
          </a>
          <span className="login-footer-separator"> | </span>
          <span>© {new Date().getFullYear()} P2S All rights reserved.</span>
        </div>
      </footer>
    </div>
  );
};

export default Login;